import React, { forwardRef } from 'react';
import cx from 'classnames';

export interface ArrowButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> {
  direction: 'prev' | 'next';
  /** Read by screen readers and shown as the tooltip. */
  label?: string;
  size?: 'sm' | 'md';
  /** `inverse` goes on dark surfaces; `solid` floats over content of unknown colour (video, a carousel). */
  tone?: 'default' | 'inverse' | 'solid';
}

const sizeClasses = { sm: 'size-8', md: 'size-10' };
const toneClasses = {
  default: 'text-ink hover:bg-surface-muted active:bg-line',
  inverse: 'text-white hover:bg-white/10 active:bg-white/20',
  solid: 'bg-white text-ink shadow-raised hover:bg-surface-muted active:bg-line',
};

/** The one previous/next control of the app: months, weeks, carousels, pages. */
export const ArrowButton = forwardRef<HTMLButtonElement, ArrowButtonProps>(
  ({ direction, label, size = 'sm', tone = 'default', type = 'button', className, ...rest }, ref) => {
    const text = label ?? (direction === 'prev' ? 'Назад' : 'Вперёд');

    return (
      <button
        ref={ref}
        type={type}
        aria-label={text}
        title={text}
        className={cx(
          'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-control transition-colors',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
          'disabled:cursor-not-allowed disabled:opacity-40',
          sizeClasses[size],
          toneClasses[tone],
          className,
        )}
        {...rest}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d={direction === 'prev' ? 'M15 18L9 12L15 6' : 'M9 6L15 12L9 18'}
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    );
  },
);

ArrowButton.displayName = 'ArrowButton';
